import type {
  InvoicePrefixNumberFormData,
  InvoiceTypeCode,
} from "@/schema/schema";
import { api } from "@/utils/axiosInterceptor";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export const useInvoicePrefix = (typeCode: InvoiceTypeCode) => {
  const {
    data: prefixNumbers,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["prefixNumbers", typeCode],
    queryFn: async () =>
      await api.get<InvoicePrefixNumberFormData[]>(`/prefix/${typeCode}`),
  });

  // const { data: generatedPrefixNumber } = useQuery({
  //   queryKey: ["generatedPrefixNumber", typeCode],
  //   queryFn: () =>
  //     api.get<GeneratedPrefixNumber>(`/prefix/generated/${typeCode}`),
  //   enabled: location !== "/prefix"
  // });

  const createPrefixNumberMutation = useMutation({
    mutationFn: (prefix: Omit<InvoicePrefixNumberFormData, "id" | "preview">) =>
      api.post<InvoicePrefixNumberFormData>("/prefix", prefix),
  });

  const updatePrefixNumberMutation = useMutation({
    mutationFn: (data: {
      id: string;
      formData: Pick<InvoicePrefixNumberFormData, "isDefault" | "typeCode"> &
        Partial<Pick<InvoicePrefixNumberFormData, "prefix" | "lastNumber">>;
    }) =>
      api.put<InvoicePrefixNumberFormData>(`/prefix/${data.id}`, data.formData),
  });

  const deletePrefixNumberMutation = useMutation({
    mutationFn: (id: string) =>
      api.delete<
        Pick<InvoicePrefixNumberFormData, "typeCode" | "prefix" | "lastNumber">
      >(`/prefix/${id}`),
  });

  const codeNumberOptions = useMemo(() => {
    if (!prefixNumbers) return undefined;
    return prefixNumbers.data.map((data) => {
      // Calculate next available number (lastNumber + 1)
      const lastNumberValue = parseInt(data.lastNumber, 10);
      const nextNumber = lastNumberValue + 1;
      const paddedNextNumber = String(nextNumber).padStart(
        data.lastNumber.length,
        "0"
      );

      return {
        id: data.id,
        codeNumber: `${data.prefix}${paddedNextNumber}`,
        isDefault: data.isDefault,
      };
    });
  }, [prefixNumbers]);

  return {
    prefixNumbers,
    codeNumberOptions,
    createPrefixNumberAsync: createPrefixNumberMutation.mutateAsync,
    isCreatingPrefix: createPrefixNumberMutation.isPending,
    updatePrefixNumberAsync: updatePrefixNumberMutation.mutateAsync,
    deletePrefixNumberAsync: deletePrefixNumberMutation.mutateAsync,
    isLoading,
    error,
  };
};

export type GeneratedPrefixNumber = {
  generatedCodeNumber: string;
};
